/**
 * @module app.ext.mcg-informed
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiRequest, IApiActionResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ApiQuery } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

@transient()
@autoinject()
export class McgInformedService {

    ////****************** Functions ************************************************////

    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger) {

        this.logger.source = this;
    }

    public async post<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.post(query) as IApiQueryResults<T>;
    }

    ////****************** Queries **************************************************////

    public async loadAssessmentList(): Promise<any> {
        const action = this.api.action('Mcg.Informed.AssessmentsList', 'ExternalSystems', {});
        return await this.api.post(action);
    }

    public async loadGuidelineList(): Promise<any> {
        const action = this.api.action('Mcg.Informed.GuidelinesList', 'ExternalSystems', {});
        return await this.api.post(action);
    }

    public async loadImportedAssessments(options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<CM.CaseAssessment>> {
        const query = this.api
            .query('CM/Cases/Assessments')
            .applyStandardLoadOptions(options)
            .include('externalSystem', 'currentVersion')
            .filterString('externalSystem.code', 'CCI');

        return await this.api.get(query);
    }

    public async loadAssessmentContent(assessmentId: string): Promise<any> {
        const action = this.api
            .action('Mcg.Informed.AssessmentContent', `CM/Cases/Assessments/${assessmentId}`, {});

        return await this.api.post(action);
    }

    public async getAssessmentContent(attachmentId: string): Promise<any> {
        const action = this.api
            .action('Mcg.Informed.Assessment', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});

        return await this.api.post(action);
    }

    public async getGuidelineContent(hsim: string, edition: string): Promise<any> {
        const externalSystem: Core.ExternalSystem = { externalResultsIdentifier: hsim, code: edition };
        const action = this.api.action('Mcg.Informed.Guideline', 'ExternalSystems', externalSystem);
        return await this.api.post(action);
    }

    public async loadAttachment(attachmentId: string, includes: string[] = []): Promise<IApiQueryResults<CM.CaseAssessmentAttachment>> {
        const query = this.api
            .query(`CM/Cases/Assessments/Attachments/${attachmentId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadGoal(id: string): Promise<any> {
        const action = this.api.action('Mcg.Informed.Goal', `CM/CarePlans/Goals/${id}`, {});
        return await this.api.post(action);
    }

    public async loadCaseMcgGoals(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/cases/${caseId}/careplan/goals`)
            .applyStandardLoadOptions(options)
            .include('problem', 'interventions', 'externalSystem')
            .includeWithState(options)
            .filterString('externalSystem.code', 'CCI');

        includes.forEach(i => {
            if(i === 'problem' || i === 'interventions' || i === 'externalSystem') {
                return;
            }
            query.include(i);
        });

        return await this.api.get(query);
    }

    public async loadPdf(hsim: string, edition: string): Promise<Blob> {
        const externalSystem: Core.ExternalSystem = { description: hsim, code: edition };
        const action: IApiRequest = this.api.action('Mcg.Informed.Pdf', `ExternalSystems`, externalSystem);
        action.responseType = "blob";
        const blob = await this.api.post(action);
        return blob;
    }

    public async loadQualityMeasures(attachmentId: string): Promise<IApiActionResults> {
        const action = this.api.action('Mcg.Informed.QualityMeasures', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    ////****************** Actions **************************************************////

    public async saveAssessmentAnswers(attachment: CM.CaseAssessmentAttachment): Promise<any> {
        const action = this.api.action('Mcg.Informed.AssessmentAnswersSave', 'CM/Cases/Assessments/Attachments', attachment);
        return await this.api.post(action);
    }

    public async saveSelectedGuidelines(caseId: string, guidelineLinks: Mcg.GuidelineLinks): Promise<any> {
        const action = this.api.action('Mcg.Informed.GuidelineLinksInsert', `CM/Cases/${caseId}`, guidelineLinks);
        return await this.api.post(action);
    }

    public async createObjects(attachment: CM.CaseAssessmentAttachment): Promise<any> {
        const action = this.api.action('Mcg.Informed.CreateObjects', 'CM/Cases/Assessments/Attachments', attachment);
        return await this.api.post(action);
    }

}
